 
import { format, parseISO, formatISO } from 'date-fns';
import {
    formatISOStartOfMonth, numeralFormatter, numeralFormatterCurrency, percentFormatter,
} from 'ns_libs/formatter';
import {
    PRO_FORMA_APARTMENT_UNIT_TYPES,
    PRO_FORMA_INDUSTRIAL_UNIT_TYPES,
    PRO_FORMA_RETAIL_AND_OFFICE_UNIT_TYPES,
    DATA_CENTER_UNIT_TYPES,
    UNIT_TYPE_NAMES,
    SQUARE_FEET_PER_ACRE,
    UnitTypeIds,
    PRO_FORMA_APARTMENT_WITH_RETAIL_UNIT_TYPES,
} from 'constants/unitTypes';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { faSearchDollar } from '@fortawesome/free-solid-svg-icons';
import { faBalanceScale, faChartMixedUpCircleDollar, faHandHoldingDollar } from '@fortawesome/pro-solid-svg-icons';
import { isApartmentModel } from 'helpers/proForma';
import { isDebtSource, isEquitySource } from 'utils/sourceTypeUtils';
import {
    PRO_FORMA_TABS, PRO_FORMA_TABLE_NAV, RETURNS_AND_WATERFALL_TABS, alwaysShowCentsCurrencyColumnKeys,
    TOTAL_ANTICIPATED_COST, TOTAL_REVISED_BUDGET,
    BOE_DISCREPANCY_METRIC_NAMES, GENERAL_INFORMATION_EXPANDABLE_SECTIONS, DEVELOPMENT_EXPANDABLE_SECTIONS,
    DISPOSITION_EXPANDABLE_SECTIONS, OPERATIONS_EXPANDABLE_SECTIONS, IN_PROGRESS_TEXT,
} from './constants';
import {
    IUpdateProFormaKeyValues, CashFlowEntry, IFormattedCashFlowPeriod,
    IProFormaValues, IOperatingPeriodCashFlows, IPeriod,
    INameValue, ISourcesSummary, IUsesSummary, ICashFlowBudgetClassification, IProFormaUses,
    IProFormaGeneralInfo, IIncomeSummary, IExpenseSummary, IDispositionData, IOperatingAndPermanentDebtInfo,
    IGrossBuildableAreaLotsData,
    IProFormaReturnsData,
    IWaterfallAnalysis,
    IKeyStatsCard,
    ISideNavFooterKeyStatDiscrepancy,
    ISourceType,
} from './types';
import { ProFormaGenericColumn } from './components/ProFormaGenericTable/types';
import { getSaleProceedsData } from './components/ProForma/ProFormaDisposition/helpers';

export const getProFormaUrl = (sideNavName: string, tabName?: string) => {
    const section = encodeURIComponent(sideNavName);
    let tab = '';

    if (!tabName) {
        if (sideNavName === PRO_FORMA_TABLE_NAV.PRO_FORMA.name) {
            tab = `&tab=${encodeURIComponent(PRO_FORMA_TABS.GENERAL_INFORMATION.name)}`;
        } else if (sideNavName === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name) {
            tab = `&tab=${encodeURIComponent(RETURNS_AND_WATERFALL_TABS.RETURNS)}`;
        }
    } else {
        tab = `&tab=${encodeURIComponent(tabName)}`;
    }

    const url = `${window.location.pathname}?section=${section}${tab}`;

    return url;
};

export const getSaleIndexForCadence = (saleMonth: number | undefined, cadence: Periods): number | undefined => {
    if (saleMonth === undefined) return undefined;
    return cadence === Periods.ANNUALLY
        ? Math.ceil((saleMonth || 0) / 12) - 1
        : saleMonth;
};

export const formatCashFlowPeriods = (
    cashFlowPeriodsByCadence: Record<Periods, string[]>,
    periodCadence: Periods = Periods.MONTHLY,
    isPlaceholderOnly?: boolean,
    maxIndex?: number,
): IFormattedCashFlowPeriod[] => {
    let formattedPeriods: {
        period: string;
        label: string;
        value: string;
        isPlaceholder?: boolean;
    }[] = [
        {
            period: 'Month #',
            label: 'MON YEAR',
            value: '',
            isPlaceholder: true,
        },
    ];
    if (isPlaceholderOnly) return formattedPeriods;

    const allPeriods = cashFlowPeriodsByCadence[periodCadence];
    const periods = maxIndex !== undefined ? allPeriods.slice(0, maxIndex + 1) : allPeriods;
    if (periodCadence === Periods.MONTHLY || periodCadence === Periods.QUARTERLY) {
        formattedPeriods = periods.map((date, index) => ({
            period: `Month ${index}`,
            label: format(parseISO(date), 'MMM yyyy'),
            value: formatISO(date, { representation: 'date' }),
        }));
    } else if (periodCadence === Periods.ANNUALLY) {
        formattedPeriods = periods.map((date, index) => ({
            period: `Year ${index}`,
            label: format(parseISO(date), 'yyyy'),
            value: formatISO(date, { representation: 'date' }),
        }));
    }
    return formattedPeriods;
};

export const generateDateRange = (
    periods: string[],
    endDate: string,
    labelFormat: string = 'MMM yyyy',
    isCashFlow: boolean = false,
    startDate?: string,
) : { label:string, value: string, isPlaceholder?:boolean }[] => {
    // "2025-04-01T00:00:00" -> "2025-04"
    // "2025-04-30T00:00:00" -> "2025-04"
    const getYearMonth = (dateStr: string) => dateStr.split('T')[0].slice(0, 7);

    let range: string[];

    if (!startDate || !endDate || periods.length === 0) {
        return [{ label: 'MON YEAR', value: '', isPlaceholder: true }];
    }

    if (endDate) {
        const endDateYearMonth = getYearMonth(endDate);
        const endDateIndex = periods?.findIndex(
            date => getYearMonth(date) === endDateYearMonth,
        );
        if (endDateIndex === -1) return [];

        const startIndex = startDate
            ? Math.max(0, periods?.findIndex(date => getYearMonth(date) === getYearMonth(startDate)))
            : 0;

        if (isCashFlow) {
            const lastIndex = Math.min(endDateIndex + 11, periods.length - 1);
            range = periods.slice(startIndex, lastIndex + 1);
        } else {
            range = periods.slice(startIndex, endDateIndex + 1);
        }
    } else {
        range = periods;
    }

    return range.map(dateString => {
        const [datePart] = (dateString || '').split('T');
        return {
            value: datePart,
            label: format(parseISO(datePart), labelFormat),
        };
    });
};

export const getLatestBudgetClassificationDate = (uses: IProFormaUses[]) => {
    let latestDate: string | null = null;

    (uses || []).map(highLevel => {
        highLevel.children?.map(subcategory => {
            subcategory.children?.map(budgetLine => {
                if (budgetLine.endDate) {
                    const budgetDate = budgetLine.endDate; // "2025-07-01T00:00:00"
                    if (!latestDate || (latestDate && budgetDate > latestDate)) {
                        latestDate = budgetDate;
                    }
                }
                return null;
            });
            return null;
        });
        return null;
    });

    return latestDate;
};

export const calculateParkingMetrics = (
    values: Pick<IProFormaValues,
        | 'structuredParkingSpaces'
        | 'surfaceParkingSpaces'
        | 'undergroundParkingSpaces'
        | 'trailerParkingSpaces'
        | 'builtGrossSquareFootage'>,
    units: number,
    modelId: number,
) => {
    // Total spots should exclude trailer parking spaces
    const totalSpotsNum = (values.structuredParkingSpaces || 0) + (values.surfaceParkingSpaces || 0) + (values.undergroundParkingSpaces || 0);
    // Return totalSpots as string "0" when 0 to avoid falsy check issues in rendering
    const totalSpots = totalSpotsNum === 0 ? '0' : totalSpotsNum;
    let ratio = totalSpotsNum === 0 ? '0.00' : undefined;
    let suffix = '/ SF';

    if ([ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(modelId)) {
        if (units && units > 0) {
            ratio = (totalSpotsNum / units).toFixed(2);
            suffix = '/ unit';
        }
    } else if (values.builtGrossSquareFootage && values.builtGrossSquareFootage > 0) {
        // Formula for non-apartment models: ratio = total spots / built gross SF / 1000
        ratio = (totalSpotsNum / (values.builtGrossSquareFootage / 1000)).toFixed(2);
    }

    return { totalSpots, ratio, suffix };
};

export const calculateEfficiencyMetrics = (grossBuildableArea: number | null, rentableSquareFootage: number): string | undefined => (
    rentableSquareFootage && grossBuildableArea
        ? ((rentableSquareFootage / grossBuildableArea)).toFixed(4)
        : undefined
);

const calculateAreaMetrics = (values: IProFormaValues, units: number) => {
    const landArea = values.landArea || 0;
    let landAreaInAcres = 0;
    if (landArea) {
        landAreaInAcres = values.landAreaUnitTypeId === UnitTypeIds.ACRE
            ? landArea
            : landArea / SQUARE_FEET_PER_ACRE;
    }

    return {
        unitsPerAcre: units && landAreaInAcres ? (units / landAreaInAcres).toFixed(2) : undefined,
    };
};

export const calculateOfficeAreaMetrics = (values: IProFormaValues) => {
    if (values.isFloorPlateAreaVaried) {
        if (!values.storyArea || values.averageStories === 0) {
            return {
                averageFloorPlate: undefined,
            };
        }

        const sumFloorAreas = values.storyArea.reduce((acc, curr) => acc + curr, 0);

        return {
            averageFloorPlate: Math.round(sumFloorAreas / values.averageStories),
        };
    }

    if (!values.builtGrossSquareFootage || values.averageStories === 0) {
        return {
            averageFloorPlate: undefined,
        };
    }

    return {
        averageFloorPlate: Math.round(values.builtGrossSquareFootage / values.averageStories),
    };
};

const createDisplayCard = (label: string, value: number | string | undefined, format?: string, valueSuffix?: string, fullWidth: boolean = false) => ({
    label, value, ...(format && { format }), ...(valueSuffix && { valueSuffix }), fullWidth,
});

const getApartmentDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
) => {
    const { totalSpots, ratio, suffix } = calculateParkingMetrics(values, units, ProFormaModelIds.APARTMENT);
    const { unitsPerAcre } = calculateAreaMetrics(values, units);

    return [
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        createDisplayCard('Units/acre', unitsPerAcre, '0,0.00'),
        createDisplayCard('Total parking spots', totalSpots, '0,0'),
        createDisplayCard('Parking ratio', ratio, '0,0.00', suffix),
    ];
};

const getApartmentWithRetailDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
) => {
    const { totalSpots, ratio, suffix } = calculateParkingMetrics(values, units, ProFormaModelIds.APARTMENT_WITH_RETAIL);
    const { unitsPerAcre } = calculateAreaMetrics(values, units);
    const totalBuiltGrossSF = (values.builtGrossSquareFootage || 0) + (values.retailBuiltGrossSquareFootage || 0);

    return [
        createDisplayCard('Total built gross SF', totalBuiltGrossSF, '0,0', 'ft²', true),
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        createDisplayCard('Units/acre', unitsPerAcre, '0,0.00'),
        createDisplayCard('Total parking spots', totalSpots, '0,0'),
        createDisplayCard('Parking ratio', ratio, '0,0.00', suffix),
    ];
};

const getOfficeDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
) => {
    const { totalSpots, ratio, suffix } = calculateParkingMetrics(values, units, ProFormaModelIds.OFFICE);
    const { averageFloorPlate } = calculateOfficeAreaMetrics(values);

    return [
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        createDisplayCard('Average floor plate', averageFloorPlate, '0,0'),
        createDisplayCard('Total parking spots', totalSpots, '0,0'),
        createDisplayCard('Parking ratio', ratio, '0,0.00', suffix),
    ];
};

const getDataCenterDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
) => {
    const { totalSpots } = calculateParkingMetrics(values, units, ProFormaModelIds.DATA_CENTER);

    return [
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        createDisplayCard('Total parking spaces', totalSpots, '0,0'),
    ];
};

const getIndustrialDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
    buildableLots?: IGrossBuildableAreaLotsData,
) => {
    const { totalSpots } = calculateParkingMetrics(values, units, ProFormaModelIds.INDUSTRIAL);
    const shouldShowCoverageAreaCard = values.averageStories === 1 || (values.isFloorPlateAreaVaried && values?.storyArea?.length);
    const firstFloorArea = values.isFloorPlateAreaVaried && values?.storyArea?.length ? values.storyArea[0] : values.builtGrossSquareFootage || 0;
    const landArea = buildableLots?.totalLandAreaSquareFootage || 0;
    const buildCoverage = landArea > 0 ? (firstFloorArea / landArea) * 100 : 0;

    return [
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        ...(shouldShowCoverageAreaCard ? [createDisplayCard('Build coverage', buildCoverage, '0,0.00', '%')] : []),
        createDisplayCard('Total parking spots', totalSpots, '0,0'),
    ];
};

const getRetailDisplayCards = (
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
    buildableLots?: IGrossBuildableAreaLotsData,
) => {
    const { totalSpots, ratio, suffix } = calculateParkingMetrics(values, units, ProFormaModelIds.RETAIL);
    const shouldShowCoverageAreaCard = values.averageStories === 1 || (values.isFloorPlateAreaVaried && values?.storyArea?.length);
    const firstFloorArea = values.isFloorPlateAreaVaried && values?.storyArea?.length ? values.storyArea[0] : values.builtGrossSquareFootage || 0;
    const landArea = buildableLots?.totalLandAreaSquareFootage || 0;
    const buildingCoverage = landArea > 0 ? (firstFloorArea / landArea) * 100 : 0;

    return [
        createDisplayCard('Net rentable area', rentableSquareFootage, '0,0'),
        ...(shouldShowCoverageAreaCard ? [createDisplayCard('Building coverage', buildingCoverage, '0,0.00', '%')] : []),
        createDisplayCard('Total parking spots', totalSpots, '0,0'),
        createDisplayCard('Parking ratio', ratio, '0,0.00', suffix),
    ];
};

export const getProFormaModelDisplayCards = (
    proFormaModelId: number,
    values: IProFormaValues,
    units: number,
    rentableSquareFootage: number,
    buildableLots?: IGrossBuildableAreaLotsData,
) => {
    switch (proFormaModelId) {
        case ProFormaModelIds.APARTMENT:
            return getApartmentDisplayCards(values, units, rentableSquareFootage);
        case ProFormaModelIds.APARTMENT_WITH_RETAIL:
            return getApartmentWithRetailDisplayCards(values, units, rentableSquareFootage);
        case ProFormaModelIds.OFFICE:
            return getOfficeDisplayCards(values, units, rentableSquareFootage);
        case ProFormaModelIds.RETAIL:
            return getRetailDisplayCards(values, units, rentableSquareFootage, buildableLots);
        case ProFormaModelIds.DATA_CENTER:
            return getDataCenterDisplayCards(values, units, rentableSquareFootage);
        case ProFormaModelIds.INDUSTRIAL:
            return getIndustrialDisplayCards(values, units, rentableSquareFootage, buildableLots);
        default:
            return [];
    }
};

export const formatValue = (
    value: string,
    isNegative = false,
    forDisplay = false,
    format?: ProFormaGenericColumn['format'],
    isHidden = false,
    showCents = false,
    customFormat?: (value: string | number) => string,
) => {
    if (isHidden) return '';

    if (format === 'none') return value;

    const num = parseFloat(value);
    if (Number.isNaN(num)) return value;
    if (!forDisplay) return value;

    const absNum = Math.abs(num);
    let formatted: string;

    switch (format) {
        case 'percentage':
            formatted = `${numeralFormatter(absNum * 100, '0,0.00')}%`;
            break;
        case 'currency':
            formatted = numeralFormatterCurrency(absNum, showCents ? '$0,0.00' : '$0,0');
            break;
        case 'custom':
            formatted = customFormat ? customFormat(absNum) : numeralFormatter(absNum);
            break;
        case 'ratio':
            formatted = `${numeralFormatter(absNum, '0,0.00')}x`;
            break;
        default:
            formatted = numeralFormatterCurrency(absNum);
    }

    return isNegative ? `(${formatted})` : formatted;
};

/**
 * Determine if cents should be shown for a given column.
 * Always show cents for certain currency columns, or when flag is true.
 */
export const shouldShowCents = (
    columnKey?: string,
    showCents: boolean = false,
): boolean => alwaysShowCentsCurrencyColumnKeys.includes(columnKey || '') || showCents;

/**
 * Includes Date logic as follows:
 *  if a user selects a start date, the sale date should be the first day of the next month.
 *  if a user selects a start date, and the start date is after the existing sale date, the sale date should be the first day of the next month.
 */
export const handleDateChange = (
    name: string,
    newDate: string | null,
    handleUpdateProForma: (keyValues: IUpdateProFormaKeyValues[]) => void,
    overwriteBudgetDates?: boolean,
) => {
    if (!newDate || typeof newDate !== 'string') {
        handleUpdateProForma([{ key: name, value: null }]);
        return;
    }

    const overwriteBudgetLineDates = { key: 'overwriteBudgetDates', value: overwriteBudgetDates || false };

    // Handle month input format (YYYY-MM)
    const [year, month] = newDate.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, 1);

    if (name === 'startDate') {
        const newStartDate = format(selectedDate, 'yyyy-MM-dd');
        const keyUpdates: IUpdateProFormaKeyValues[] = [{ key: 'startDate', value: `${newStartDate}T00:00:00` }, overwriteBudgetLineDates];
        handleUpdateProForma(keyUpdates);
    } else if (name === 'constructionStart') {
        const newConstructionStartDate = format(selectedDate, 'yyyy-MM-dd');
        handleUpdateProForma([{ key: 'constructionStart', value: `${newConstructionStartDate}T00:00:00` }, overwriteBudgetLineDates]);
    }
};

export const getOverwriteBudgetDatesModalText = (areBudgetLineDatesOutOfBounds: boolean) => {
    let header = 'Maintain/overwrite existing budget line dates';
    const title = 'Key dates impact budget line start and end dates used in Uses projections.';
    let subtitle = `Select whether you would like to overwrite your budget line 
        dates using new key date information or keep the existing budget line dates.`;

    if (areBudgetLineDatesOutOfBounds) {
        header = 'Overwrite existing budget line dates';
        subtitle = `At least one existing budget line date is out of bounds of new key dates. 
            Budget line dates will be updated accordingly.`;
    }

    return { header, title, subtitle };
};

export const getProFormaDevelopmentUnitTypeOptions = (proFormaModelId: number) => {
    if (proFormaModelId === ProFormaModelIds.APARTMENT) {
        return PRO_FORMA_APARTMENT_UNIT_TYPES.map(unitType => ({
            label: UNIT_TYPE_NAMES[unitType].dropdownItemName,
            value: unitType,
        }));
    }
    if (proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL) {
        return PRO_FORMA_APARTMENT_WITH_RETAIL_UNIT_TYPES.map(unitType => ({
            label: UNIT_TYPE_NAMES[unitType].dropdownItemName,
            value: unitType,
        }));
    }
    if (proFormaModelId === ProFormaModelIds.INDUSTRIAL) {
        return PRO_FORMA_INDUSTRIAL_UNIT_TYPES.map(unitType => ({
            label: UNIT_TYPE_NAMES[unitType].dropdownItemName,
            value: unitType,
        }));
    }
    if (proFormaModelId === ProFormaModelIds.RETAIL || proFormaModelId === ProFormaModelIds.OFFICE) {
        return PRO_FORMA_RETAIL_AND_OFFICE_UNIT_TYPES.map(unitType => ({
            label: UNIT_TYPE_NAMES[unitType].dropdownItemName,
            value: unitType,
        }));
    }
    if (proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        return DATA_CENTER_UNIT_TYPES.map(unitType => ({
            label: UNIT_TYPE_NAMES[unitType].dropdownItemName,
            value: unitType,
        }));
    }
    return [];
};

export const calculateFloorAreaRatio = (
    landArea: number | null,
    landAreaUnitType: number,
    grossBuildableArea: number,
) => {
    if (landArea === null) return null;
    const landAreaSf = landAreaUnitType === UnitTypeIds.ACRE ? Number(landArea) * SQUARE_FEET_PER_ACRE : Number(landArea);
    return landAreaSf ? grossBuildableArea / landAreaSf : 0;
};

// Creates the monthly columns for our table
export const createCashFlowColumns = (cashFlow: CashFlowEntry[] = []): ProFormaGenericColumn[] => cashFlow.map(cf => ({
    key: `column_${cf.month}`,
    field: `column_${cf.month}`,
    header: cf.month,
    isFixed: false,
}));

// Turns monthly numbers into table-friendly data
export const createCashFlowData = (
    cashFlow: CashFlowEntry[] = [],
): Record<string, string> => Object.fromEntries(
    cashFlow.map(cf => [`column_${cf.month}`, String(cf.value || 0)]),
);

export const getPeriods = <T extends { name: string }>(
    cashFlowPeriods: string[],
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[],
    items: T[],
    valueKey: 'units' | 'rentAdjustments' | 'otherIncome' | 'incomeAdjustments' | 'operatingExpenses' | 'capitalExpenditures' | 'otherCapitalExpenditures',
    operationBeginMonthIndex?: number,
    isPlaceholder?: boolean,
): IPeriod[] => {
    if (isPlaceholder) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: {} as Record<string, string | null>,
        }];
    }

    if (!cashFlowPeriods?.length || !items?.length) {
        return [];
    }

    let emptyValue = '-';

    if (operationBeginMonthIndex !== null && operationBeginMonthIndex !== undefined) {
        const cashFlowValues = operatingPeriodCashFlows[operationBeginMonthIndex]?.[valueKey] as INameValue[] | undefined;
        let desiredValue;
        try {
            desiredValue = cashFlowValues?.find(v => v.name === items[0]?.name)?.value;
            emptyValue = desiredValue !== null && desiredValue !== undefined ? '0' : '-';
        } catch {
            emptyValue = '-';
        }
    }

    const periods = cashFlowPeriods?.map((periodStartDate, index) => {
        if (!periodStartDate) return null;

        const cashFlowPeriod = operatingPeriodCashFlows[index];
        const values = items?.reduce((acc, item) => {
            if (!item?.name) return acc;

            const valueArray = cashFlowPeriod?.[valueKey] as INameValue[] | undefined;
            const foundValue = valueArray?.find(v => v.name === item.name)?.value;
            const value = foundValue !== null && foundValue !== undefined ? String(foundValue) : emptyValue;
            return {
                ...acc,
                [item.name]: value,
            };
        }, {} as Record<string, string | null>);

        try {
            return {
                periodName: format(parseISO(periodStartDate), 'MMM yyyy'),
                periodDate: periodStartDate,
                values,
            } as IPeriod;
        } catch {
            return null;
        }
    }) || [];

    return periods.filter((period): period is IPeriod => period !== null);
};

// Formats currency and add parentheses for values where the table does not add them properly
export const formatCurrencyWithParentheses = (value: number, showCents: boolean = false) => {
    const absValue = Math.abs(value);
    const formatted = numeralFormatterCurrency(absValue, showCents ? '$0,0.00' : '$0,0');
    return value < 0 ? `(${formatted})` : formatted;
};

export const checkUsesSourcesUnequal = (
    uses: IUsesSummary,
    sources: ISourcesSummary,
) => (uses?.totals?.amount != null
    && sources?.totals?.amount != null
    && Number(uses?.totals?.amount).toFixed(2) !== Number(sources?.totals?.amount).toFixed(2));

export const convertSignUpDataToProFormaValues = (signUpProForma: any) => {
    const basicInfo = signUpProForma.basicInformation;

    return {
        id: 0,
        dealName: basicInfo.dealName,
        address: basicInfo.address,
        city: basicInfo.city,
        county: basicInfo.county,
        state: basicInfo.state,
        zipCode: basicInfo.zipCode,
        startDate: new Date(basicInfo.startDate).toISOString().split('T')[0],
        constructionStart: new Date(basicInfo.constructionStart).toISOString().split('T')[0],
        constructionDuration: basicInfo.constructionDuration,
        holdPeriod: basicInfo.holdPeriod,
        saleDate: basicInfo.saleDate,
        saleMonth: basicInfo.saleMonth,
        landArea: basicInfo.landArea,
        landAreaUnitTypeId: basicInfo.landAreaUnit === 'sqft' ? 1 : 2,
        floorAreaRatio: basicInfo.floorAreaRatio,
        grossBuildableArea: basicInfo.grossBuildableArea,
        buildings: basicInfo.buildings,
        averageStories: basicInfo.stories,
        averageClearHeight: null,
        isFloorPlateAreaVaried: null,
        storyArea: null,
        builtGrossSquareFootage: basicInfo.builtGrossSquareFootage,
        structuredParkingSpaces: basicInfo.structuredParkingSpaces,
        surfaceParkingSpaces: basicInfo.surfaceParkingSpaces,
        undergroundParkingSpaces: basicInfo.undergroundParkingSpaces,
        trailerParkingSpaces: basicInfo.trailerParkingSpaces,
        buildableLots: basicInfo.buildableLots,
    };
};

export const getProjectUsesActualsHeaders = (forecastMetric: string, isEarlyPlanningProject: boolean) => {
    let totalBalanceHeaderName = forecastMetric === TOTAL_ANTICIPATED_COST ?
        TOTAL_ANTICIPATED_COST : TOTAL_REVISED_BUDGET;
    let balanceRemainingHeaderName = forecastMetric === TOTAL_ANTICIPATED_COST ?
        'Balance Remaining (Anticipated)' : 'Balance Remaining (Budget)';

    if (isEarlyPlanningProject) {
        totalBalanceHeaderName = 'Early Budget Scenario';
        balanceRemainingHeaderName = 'Balance Remaining (Scenario)';
    }

    return {
        totalBalanceHeaderName,
        balanceRemainingHeaderName,
    };
};

export const getProjectUsesActualsTotals = (
    forecastMetric: string, uses: ICashFlowBudgetClassification[],
    isEarlyPlanningProject: boolean, selectedScenario: number | null,
) => {
    const totalBudgetField = forecastMetric === TOTAL_ANTICIPATED_COST ? 'totalAnticipatedCost' : 'totalRevisedBudget';
    let totalBudget = (uses || []).reduce((sum, item) => sum + (+item[totalBudgetField]! || 0), 0);

    const totalBalanceRemainingField = forecastMetric === TOTAL_ANTICIPATED_COST ? 'balanceRemainingAnticipated' : 'balanceRemaining';
    let totalBalanceRemaining = uses.reduce((sum, item) => sum + (+item[totalBalanceRemainingField]! || 0), 0);

    if (isEarlyPlanningProject) {
        totalBalanceRemaining = uses.reduce((sum, { totalSpentToDate = 0 }) => sum + (+totalSpentToDate || 0), 0);

        let updatedScenariosTotalBudget = 0;

        uses.map(use => {
            use.scenarios?.map(scenario => {
                if (scenario.id === selectedScenario) {
                    updatedScenariosTotalBudget += scenario?.value;
                }
                return null;
            });
            return null;
        });
        totalBudget = updatedScenariosTotalBudget;
    }

    return {
        totalBudget,
        totalBalanceRemaining,
    };
};

export const getProjectActualsCategoryLevelTotals = (
    forecastMetric: string, category: ICashFlowBudgetClassification,
    isEarlyPlanningProject: boolean, selectedScenario: number | null,
) => {
    let budgetTotal = forecastMetric === TOTAL_ANTICIPATED_COST ? category.totalAnticipatedCost : category.totalRevisedBudget;
    let balanceRemaining = forecastMetric === TOTAL_ANTICIPATED_COST ? category.balanceRemainingAnticipated : category.balanceRemaining;

    if (isEarlyPlanningProject && selectedScenario) {
        const targetScenario = category?.scenarios?.find(scenario => scenario.id === selectedScenario);
        if (targetScenario) {
            budgetTotal = targetScenario.value || 0;
        }
        balanceRemaining = category.totalSpentToDate || 0;
    }

    return {
        budgetTotal,
        balanceRemaining,
    };
};

export const getEstimateRentalPeriods = <T extends { floor: number }>(
    cashFlowPeriods: string[],
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[],
    items: T[],
    operationBeginMonthIndex?: number,
    isPlaceholder?: boolean,
): IPeriod[] => {
    if (isPlaceholder) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: {} as Record<string, string | null>,
        }];
    }

    if (!cashFlowPeriods?.length || !items?.length) {
        return [];
    }

    let emptyValue = '-';
    if (operationBeginMonthIndex !== null && operationBeginMonthIndex !== undefined) {
        const cashFlowPeriod = operatingPeriodCashFlows[operationBeginMonthIndex];
        const floorValue = cashFlowPeriod?.incomeFloorEstimates?.find((f: { floor: number, value: number | null }) => f.floor === items[0]?.floor)?.value;
        emptyValue = floorValue !== null && floorValue !== undefined ? '0' : '-';
    }

    return cashFlowPeriods.map((periodStartDate, periodIndex) => {
        if (!periodStartDate) return null;

        const cashFlowPeriod = operatingPeriodCashFlows[periodIndex];
        const values = items.reduce((acc, item) => {
            if (!item?.floor) return acc;

            const floorValue = cashFlowPeriod?.incomeFloorEstimates?.find((f: { floor: number, value: number | null }) => f.floor === item.floor)?.value;
            const value = floorValue !== null && floorValue !== undefined ? String(floorValue) : emptyValue;

            return {
                ...acc,
                [String(item.floor)]: value,
            };
        }, {} as Record<string, string | null>);

        try {
            return {
                periodName: format(parseISO(periodStartDate), 'MMM yyyy'),
                periodDate: periodStartDate,
                values,
            } as IPeriod;
        } catch {
            return null;
        }
    }).filter((period): period is IPeriod => period !== null);
};

export const getPrimitivePeriods = (
    cashFlowPeriods: string[],
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[],
    valueKey: keyof IOperatingPeriodCashFlows,
    operationBeginMonthIndex?: number,
    isPlaceholder?: boolean,
): IPeriod[] => {
    if (isPlaceholder) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: { value: '-' },
        }];
    }

    if (!cashFlowPeriods?.length) {
        return [];
    }

    let emptyValue = '-';
    if (operationBeginMonthIndex !== null && operationBeginMonthIndex !== undefined) {
        const cashFlowPeriod = operatingPeriodCashFlows[operationBeginMonthIndex];
        const value = cashFlowPeriod?.[valueKey];
        emptyValue = value !== null && value !== undefined ? '0' : '-';
    }

    return cashFlowPeriods.map((periodStartDate, periodIndex) => {
        if (!periodStartDate) return null;

        const cashFlowPeriod = operatingPeriodCashFlows[periodIndex];
        const value = cashFlowPeriod?.[valueKey];
        const formattedValue = value !== null && value !== undefined ? String(value) : emptyValue;

        try {
            return {
                periodName: format(parseISO(periodStartDate), 'MMM yyyy'),
                periodDate: periodStartDate,
                values: { value: formattedValue },
            } as IPeriod;
        } catch {
            return null;
        }
    }).filter((period): period is IPeriod => period !== null);
};

export const getOtherIncomePeriods = <T extends { name: string; startMonth?: number | null }>(
    cashFlowPeriods: string[],
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[],
    items: T[],
    isPlaceholder?: boolean,
): IPeriod[] => {
    if (isPlaceholder) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: {} as Record<string, string | null>,
        }];
    }

    if (!cashFlowPeriods?.length || !items?.length) {
        return [];
    }

    const incomeEmptyValues = items.reduce((acc, item) => {
        if (!item?.name) return acc;
        if (item.startMonth !== null && item.startMonth !== undefined) {
            const startMonthIndex = item.startMonth;
            if (startMonthIndex !== -1) {
                const cashFlowPeriod = operatingPeriodCashFlows[startMonthIndex];
                const incomeValue = cashFlowPeriod?.otherIncome?.find(i => i.name === item.name)?.value;
                acc[item.name] = incomeValue !== null && incomeValue !== undefined ? '0' : '-';
            } else {
                acc[item.name] = '-';
            }
        } else {
            acc[item.name] = '-';
        }
        return acc;
    }, {} as Record<string, string>);

    return cashFlowPeriods.map((periodStartDate, periodIndex) => {
        if (!periodStartDate) return null;

        const cashFlowPeriod = operatingPeriodCashFlows[periodIndex];
        const values = items.reduce((acc, item) => {
            if (!item?.name) return acc;

            const incomeValue = cashFlowPeriod?.otherIncome?.find(i => i.name === item.name)?.value;

            let value = incomeEmptyValues[item.name] || '-';
            if (incomeValue !== null && incomeValue !== undefined) {
                value = String(incomeValue);
            }

            return {
                ...acc,
                [item.name]: value,
            };
        }, {} as Record<string, string | null>);

        try {
            return {
                periodName: format(parseISO(periodStartDate), 'MMM yyyy'),
                periodDate: periodStartDate,
                values,
            } as IPeriod;
        } catch {
            return null;
        }
    }).filter((period): period is IPeriod => period !== null);
};

// ensure we're rounding to 2 decimals before comparing. We're only showing 2 decimals or whole numbers in the UI
const isValueDiscrepant = (a?: number | string, b?: number | string) => Number(Number(a).toFixed(2)) !== Number(Number(b).toFixed(2));

export const getGeneralInfoDiscrepancies = (
    proForma: IProFormaGeneralInfo,
    incomeSummary: IIncomeSummary,
    boeScenario: BOEScenario | undefined,
) => {
    const metricNames: string[] = [];
    const efficiency = calculateEfficiencyMetrics(Number(proForma.grossBuildableArea), incomeSummary.totalRentableSf || 0);

    if (efficiency !== undefined &&
            isValueDiscrepant(efficiency, boeScenario?.buildingEfficiency || 0) &&
                proForma.proFormaModelId !== ProFormaModelIds.DATA_CENTER) {
        metricNames.push(BOE_DISCREPANCY_METRIC_NAMES.EFFICIENCY_RATIO);
    }

    return {
        [GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].number]: metricNames,
    };
};

export const getDevelopmentDiscrepancies = (
    totalUses: number,
    totalEquity: number,
    totalDebt: number,
    boeScenario: BOEScenario | undefined,
) => {
    const usesMetricNames: string[] = [];
    const sourcesMetricNames: string[] = [];

    if (isValueDiscrepant(totalEquity, boeScenario?.requiredEquity || 0)) {
        sourcesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_EQUITY);
    }

    if (isValueDiscrepant(totalDebt, boeScenario?.loanAmount || 0)) {
        sourcesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_CONSTRUCTION_DEBT);
    }

    if (isValueDiscrepant(totalUses, boeScenario?.totalDevelopmentCosts || 0)) {
        usesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_DEVELOPMENT_COSTS);
    }

    return {
        [DEVELOPMENT_EXPANDABLE_SECTIONS[1].number]: usesMetricNames,
        [DEVELOPMENT_EXPANDABLE_SECTIONS[2].number]: sourcesMetricNames,
    };
};

export const getOperationsDiscrepancies = (
    proForma: IProFormaGeneralInfo,
    incomeSummary: IIncomeSummary,
    expenseSummary: IExpenseSummary,
    operations: IOperatingAndPermanentDebtInfo,
    boeScenario: BOEScenario | undefined,
) => {
    const incomeMetricNames: string[] = [];
    const expensesMetricNames: string[] = [];

    const isEstimate = operations.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const boeUnits = Number(boeScenario?.numberOfUnits);
    const modelId = proForma.proFormaModelId;

    if ([ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(modelId)) {
        if (isValueDiscrepant(incomeSummary.grossRent?.totalUnits, boeUnits)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_UNITS);
        }
        if (isValueDiscrepant(incomeSummary.grossRent?.totalAmountPerYear, boeScenario?.grossPotentialRent || 0)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.GROSS_RENT);
        }
    } else if ([ProFormaModelIds.INDUSTRIAL, ProFormaModelIds.OFFICE, ProFormaModelIds.RETAIL].includes(modelId)) {
        if (isEstimate) {
            if (isValueDiscrepant(incomeSummary.incomeFloorEstimates?.length, boeUnits)) {
                incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_FLOORS);
            }
        } else if (isValueDiscrepant(incomeSummary.incomeTenants?.length, boeUnits)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_SUITES);
        }

        if (isValueDiscrepant(incomeSummary.totalRentalIncome?.totalAnnualRent, boeScenario?.grossPotentialRent || 0)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_RENTAL_INCOME);
        }
    } else if (modelId === ProFormaModelIds.DATA_CENTER) {
        if (isEstimate) {
            if (isValueDiscrepant(incomeSummary.incomeFloorEstimatesDataCenter?.length, boeUnits)) {
                incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_FLOORS);
            }
        } else if (isValueDiscrepant(incomeSummary.incomeTenantsDataCenter?.length, boeUnits)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_TENANTS);
        }

        if (isValueDiscrepant(incomeSummary.totalRentalIncome?.totalAnnualRent, boeScenario?.grossPotentialRent || 0)) {
            incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_RENTAL_INCOME);
        }
    }

    if (isValueDiscrepant(expenseSummary.totalOperatingExpenses?.totalAmountPerYear, boeScenario?.expensesTotal || 0)) {
        expensesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_EXPENSES);
    }

    if (isValueDiscrepant(expenseSummary.netOperatingIncome, boeScenario?.netOperatingIncome || 0)) {
        expensesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.NET_OPERATING_INCOME);
    }

    return {
        [OPERATIONS_EXPANDABLE_SECTIONS[1].number]: incomeMetricNames,
        [OPERATIONS_EXPANDABLE_SECTIONS[2].number]: expensesMetricNames,
    };
};

export const getOperationsWizardDiscrepancies = (
    proFormaModelId: number,
    totalUnitMixUnits: number,
    tenants: number,
    totalAnnualRentalIncome: number,
    totalExpenses: number,
    boeScenario: BOEScenario | undefined,
) => {
    const incomeMetricNames: string[] = [];
    const expensesMetricNames: string[] = [];

    const totalUnits = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId) ? totalUnitMixUnits : tenants;

    if (isValueDiscrepant(totalUnits, Number(boeScenario?.numberOfUnits))) {
        incomeMetricNames.push(
            [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId) ?
                BOE_DISCREPANCY_METRIC_NAMES.TOTAL_UNITS :
                BOE_DISCREPANCY_METRIC_NAMES.TOTAL_TENANTS,
        );
    }

    if (isValueDiscrepant(totalAnnualRentalIncome, boeScenario?.grossPotentialRent || 0)) {
        incomeMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_RENTAL_INCOME);
    }

    if (isValueDiscrepant(totalExpenses, boeScenario?.expensesTotal || 0)) {
        expensesMetricNames.push(BOE_DISCREPANCY_METRIC_NAMES.TOTAL_EXPENSES);
    }

    return {
        [OPERATIONS_EXPANDABLE_SECTIONS[1].number]: incomeMetricNames,
        [OPERATIONS_EXPANDABLE_SECTIONS[2].number]: expensesMetricNames,
    };
};

export const getDispositionDiscrepancies = (
    disposition: IDispositionData | undefined,
    boeScenario: BOEScenario | undefined,
) => {
    const metricNames: string[] = [];

    if (isValueDiscrepant(disposition?.stabilizedValue?.untrended, boeScenario?.exitValuation || 0)) {
        metricNames.push(BOE_DISCREPANCY_METRIC_NAMES.STABILIZED_VALUE);
    }

    return {
        [DISPOSITION_EXPANDABLE_SECTIONS[3].number]: metricNames,
    };
};

export const getBOEProFormaDiscrepancies = (
    proForma: IProFormaGeneralInfo,
    uses: IUsesSummary,
    sources: ISourcesSummary,
    incomeSummary: IIncomeSummary,
    expenseSummary: IExpenseSummary,
    disposition: IDispositionData | undefined,
    operations: IOperatingAndPermanentDebtInfo,
    boeScenario: BOEScenario | undefined,
    sourceTypes: ISourceType[] | undefined,
) => {
    const totalEquity = sources?.sources
        ?.filter(source => isEquitySource(source.sourceTypeId, sourceTypes))
        ?.reduce((acc, source) => acc + Number(source.amount), 0) || 0;

    const totalDebt = sources?.sources
        ?.filter(source => isDebtSource(source.sourceTypeId, sourceTypes))
        ?.reduce((acc, source) => acc + Number(source.amount), 0) || 0;

    return {
        [PRO_FORMA_TABS.GENERAL_INFORMATION.name]: getGeneralInfoDiscrepancies(proForma, incomeSummary, boeScenario),
        [PRO_FORMA_TABS.DEVELOPMENT.name]: getDevelopmentDiscrepancies(uses?.totals?.amount || 0, totalEquity, totalDebt, boeScenario),
        [PRO_FORMA_TABS.OPERATIONS.name]: getOperationsDiscrepancies(proForma, incomeSummary, expenseSummary, operations, boeScenario),
        [PRO_FORMA_TABS.DISPOSITION.name]: getDispositionDiscrepancies(disposition, boeScenario),
    };
};

export const getPeriodsOptions = (startDate: string, saleDate: string, monthlyPeriodOptions: IFormattedCashFlowPeriod[]) => {
    /** Helper is used to populate the list of monthly period options between the pro forma start and sale dates, inclusive.
     * This ensures that the full valid date range is available for PeriodsSelect dropdowns,
     * regardless of current * budget classification start/end selection.
     * @param startDate - The pro forma start date (ISO string)
     * @param saleDate - The pro forma sale date (ISO string)
     * @param monthlyPeriodOptions - Full list of available cash flow periods (formatted)
     * @returns An array of formatted period options within the inclusive date range */
    if (!monthlyPeriodOptions.length) return [];

    const formattedStartDate = formatISOStartOfMonth(startDate);
    const formattedEndDate = formatISOStartOfMonth(saleDate);

    const startIndex = monthlyPeriodOptions.findIndex(option => option.value === formattedStartDate);
    const endIndex = monthlyPeriodOptions.findIndex(option => option.value === formattedEndDate);

    return monthlyPeriodOptions.slice(startIndex, endIndex + 1);
};

export const getDiscrepancyColors = (proFormaValue: number, projectActualsValue: number) => {
    if (projectActualsValue === 0 || proFormaValue === projectActualsValue) {
        return { iconColor: 'text-primary', cardColor: '' };
    }

    return {
        iconColor: projectActualsValue < proFormaValue ? 'text-danger' : 'text-success',
        cardColor: projectActualsValue < proFormaValue ? '--danger' : '--success',
    };
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const getKeyStatsCardValue = (value: number | null, formatter: Function | null, appendValue: string = '') => {
    const formatFn = formatter || ((v: number) => numeralFormatter(v, '0,0.00'));
    if (value === null || value === undefined) return IN_PROGRESS_TEXT;
    return `${formatFn(value)}${appendValue}`;
};

export const getDefaultFooterKeyStats = (returnsData: IProFormaReturnsData, showCentsFormat: string, disposition?: IDispositionData) => {
    const equityProceedsData = getSaleProceedsData(disposition, 'equityProceedsFromSale');
    const equityProceeds = equityProceedsData?.amount ? equityProceedsData.amount : null;

    const defaultKeyStats: IKeyStatsCard[][] = [
        [
            {
                icon: faSearchDollar,
                title: 'Levered IRR',
                formatter: v => getKeyStatsCardValue(v == null ? null : v * 100, percentFormatter),
                value: returnsData?.netLeveredIrr,
                comparisonValue: Number(((returnsData?.netLeveredIrr || 0) * 100).toFixed(2)),
                marginClass: 'mb-1',
            },
            {
                icon: faChartMixedUpCircleDollar,
                title: 'Levered EMx',
                formatter: v => getKeyStatsCardValue(v, null, 'x'),
                value: returnsData?.netLeveredEquityMultiple,
                comparisonValue: Number((returnsData?.netLeveredEquityMultiple || 0).toFixed(2)),
                marginClass: 'mb-1',
            },
        ],
        [
            {
                icon: faBalanceScale,
                title: 'Stabilized yield-on-cost',
                formatter: v => getKeyStatsCardValue(v == null ? null : v * 100, percentFormatter),
                value: returnsData?.yieldOnCost?.trended,
                comparisonValue: Number(((returnsData?.yieldOnCost?.trended || 0) * 100).toFixed(2)),
                marginClass: 'mb-0',
            },
            {
                icon: faHandHoldingDollar,
                title: 'Equity proceeds',
                formatter: val => getKeyStatsCardValue(val, (v: number) => numeralFormatterCurrency(v, showCentsFormat)),
                value: equityProceeds,
                comparisonValue: Number((equityProceeds || 0).toFixed(0)),
                marginClass: 'mb-0',
            },
        ],
    ];

    return defaultKeyStats;
};

export const getPartnerReturnFooterKeyStats = (waterfallAnalysis: IWaterfallAnalysis, showCentsFormat: string) => {
    const gpIrrValue = waterfallAnalysis?.waterfallSummary?.gp1Irr ? parseFloat(waterfallAnalysis.waterfallSummary.gp1Irr) : null;
    const lpIrrValue = waterfallAnalysis?.waterfallSummary?.lpIrr ? parseFloat(waterfallAnalysis.waterfallSummary.lpIrr) : null;
    const gpNetProfitValue = waterfallAnalysis?.waterfallSummary?.gp1NetProfit ? parseFloat(waterfallAnalysis.waterfallSummary.gp1NetProfit) : null;
    const lpNetProfitValue = waterfallAnalysis?.waterfallSummary?.lpNetProfit ? parseFloat(waterfallAnalysis.waterfallSummary.lpNetProfit) : null;

    const partnerReturnKeyStats: IKeyStatsCard[][] = [
        [
            {
                icon: faHandHoldingDollar,
                title: 'General partner IRR',
                formatter: v => getKeyStatsCardValue(v !== null && !Number.isNaN(v) ? v * 100 : null, percentFormatter),
                value: gpIrrValue,
                comparisonValue: Number((gpIrrValue !== null && !Number.isNaN(gpIrrValue) ? gpIrrValue * 100 : 0).toFixed(2)),
                marginClass: 'my-1',
            },
            {
                icon: faBalanceScale,
                title: 'Limited partner IRR',
                formatter: v => getKeyStatsCardValue(v !== null && !Number.isNaN(v) ? v * 100 : null, percentFormatter),
                value: lpIrrValue,
                comparisonValue: Number((lpIrrValue !== null && !Number.isNaN(lpIrrValue) ? lpIrrValue * 100 : 0).toFixed(2)),
                marginClass: 'my-1',
            },
        ],
        [
            {
                icon: faHandHoldingDollar,
                title: 'GP net cash flow',
                formatter: val => getKeyStatsCardValue(
                    !Number.isNaN(val) ? val : null, (v: number) => numeralFormatterCurrency(v, showCentsFormat),
                ),
                value: gpNetProfitValue,
                comparisonValue: Number((gpNetProfitValue !== null && !Number.isNaN(gpNetProfitValue) ? gpNetProfitValue : 0).toFixed(0)),
                marginClass: 'mb-0',
            },
            {
                icon: faBalanceScale,
                title: 'LP net cash flow',
                formatter: val => getKeyStatsCardValue(
                    !Number.isNaN(val) ? val : null, (v: number) => numeralFormatterCurrency(v, showCentsFormat),
                ),
                value: lpNetProfitValue,
                comparisonValue: Number((lpNetProfitValue !== null && !Number.isNaN(lpNetProfitValue) ? lpNetProfitValue : 0).toFixed(0)),
                marginClass: 'mb-0',
            },
        ],
    ];

    return partnerReturnKeyStats;
};

export const getKeyStatsActualsDiscrepancies = (
    proFormaKeyStats: IKeyStatsCard[],
    proFormaPartnerReturnKeyStats: IKeyStatsCard[],
    projectActualsKeyStats: IKeyStatsCard[],
    projectActualsPartnerReturnKeyStats: IKeyStatsCard[],
) => {
    const discrepancies: Record<string, ISideNavFooterKeyStatDiscrepancy> = {};

    proFormaKeyStats?.forEach((proFormaStat, index) => {
        const projectActualsStat = projectActualsKeyStats[index];
        if (proFormaStat.comparisonValue !== projectActualsStat.comparisonValue) {
            const { cardColor, iconColor } = getDiscrepancyColors(proFormaStat.comparisonValue, projectActualsStat.comparisonValue);
            discrepancies[proFormaStat.title] = { cardColor, iconColor };
        }
    });

    proFormaPartnerReturnKeyStats?.forEach((proFormaStat, index) => {
        const projectActualsStat = projectActualsPartnerReturnKeyStats[index];
        if (proFormaStat.comparisonValue !== projectActualsStat.comparisonValue) {
            const { cardColor, iconColor } = getDiscrepancyColors(proFormaStat.comparisonValue, projectActualsStat.comparisonValue);
            discrepancies[proFormaStat.title] = { cardColor, iconColor };
        }
    });

    return discrepancies;
};

export const doesRentableSFExceedBuiltGSF = (
    proFormaModelId: number, builtGrossSquareFootage: number | null,
    incomeSummary: IIncomeSummary, incomeCalculationType: string,
) => {
    const isRSFMoreThanBuiltGSF = (incomeSummary.totalRentableSf || 0) > (builtGrossSquareFootage || 0);

    const isApartment = isApartmentModel(proFormaModelId);
    const showWarning = !!incomeSummary.totalRentableSf && !!builtGrossSquareFootage &&
        (isApartment || (!isApartment && incomeCalculationType !== IncomeCalculationType.ESTIMATE));

    return showWarning && isRSFMoreThanBuiltGSF;
};

export const calculateNetRentableArea = (
    proFormaModelId: number,
    builtGrossSquareFootage: number,
    efficiencyRatio: number | null | undefined,
    incomeSummary: { unitMix?: unknown[]; totalRentableSf?: number },
): number => {
    const isApartment = isApartmentModel(proFormaModelId);

    return isApartment && (incomeSummary.unitMix?.length || 0) < 2
        ? builtGrossSquareFootage * (efficiencyRatio || 0)
        : incomeSummary.totalRentableSf || 0;
};
